import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import { appInfo } from '@/lib/appInfo';
import { Link } from 'react-router-dom';

interface HelpSupportPanelProps { open: boolean; onClose: () => void }

const faqs = [
  { q: 'How do I ask LAVYN a question?', a: 'Type your question or attach a photo, then tap Generate Lesson to create a teaching video. Tutor is available from the bottom navigation for chat-based help.' },
  { q: 'How do I use the study tools?', a: 'Open Practice, Study Cards, or Quick Learn on Home and choose a topic or source. Smart Calculator accepts expressions and shows the result.' },
  { q: 'What if a lesson fails to load?', a: 'Check your connection and follow the retry option shown by LAVYN. Include the error message and the steps you took when reporting a problem. Check important AI answers with your class notes.' },
];

export default function HelpSupportPanel({ open, onClose }: HelpSupportPanelProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const text = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const muted = isDark ? 'text-[#A1A1A6]' : 'text-neutral-500';
  const card = isDark ? 'border-white/[0.05] bg-white/[0.03]' : 'border-neutral-100 bg-neutral-50';
  return (
    <AnimatePresence>
      {open && <div className="fixed inset-0 z-[100] flex items-end lg:items-center lg:justify-end">
        <motion.div className={`absolute inset-0 ${isDark ? 'bg-black/60' : 'bg-black/30'}`}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
        <motion.div className={`relative w-full lg:w-[400px] lg:h-full ${isDark ? 'bg-[#161618] border-white/[0.05]' : 'bg-white border-neutral-200'} lg:border-l overflow-hidden flex flex-col`}
          initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 320, damping: 30 }}
          style={{ maxHeight: '85vh', borderRadius: '24px 24px 0 0' }}>
          <div className="flex justify-center pt-3 pb-1 lg:hidden"><div className={`w-10 h-1 rounded-full ${isDark ? 'bg-white/20' : 'bg-black/20'}`} /></div>
          <div className="flex items-center justify-between px-5 pt-2 pb-3">
            <h3 className={`text-base font-semibold ${text}`}>Help &amp; Support</h3>
            <Pressable onClick={onClose} aria-label="Close Help & Support" scale={0.88}
              className={`w-9 h-9 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'} ${muted}`}>
              <i className="ri-close-line" />
            </Pressable>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-8">
            <h4 className={`text-sm font-semibold mb-3 ${text}`}>Help with LAVYN</h4>
            <div className="space-y-2">
              {faqs.map((faq, index) => <div key={faq.q} className={`rounded-xl border overflow-hidden ${card}`}>
                <Pressable onClick={() => setExpandedFaq(expandedFaq === index ? null : index)} aria-expanded={expandedFaq === index}
                  scale={0.98} className={`w-full text-left px-4 py-3.5 text-sm font-medium ${text}`}>
                  {faq.q}
                </Pressable>
                <AnimatePresence>{expandedFaq === index && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .25 }} className="overflow-hidden">
                  <p className={`px-4 pb-4 text-[13px] leading-relaxed ${muted}`}>{faq.a}</p>
                </motion.div>}</AnimatePresence>
              </div>)}
            </div>
            <div className={`rounded-2xl border p-4 mt-5 ${card}`}>
              <h4 className={`text-sm font-semibold ${text}`}>Report a problem / contact support</h4>
              {appInfo.supportEmail ? <a className={`block mt-3 text-sm underline underline-offset-4 ${text}`}
                href={`mailto:${appInfo.supportEmail}?subject=${encodeURIComponent('LAVYN support')}&body=${encodeURIComponent(`App version: ${appInfo.version}\n\nWhat happened?\n\nSteps to reproduce:\n`)}`}>Email LAVYN support</a>
                : <p className={`text-xs mt-2 leading-relaxed ${muted}`}>A support contact has not been configured yet. No message has been sent.</p>}
            </div>
            <div className={`mt-5 flex flex-col gap-4 text-sm ${text}`}>
              <Link to="/contact" onClick={onClose} className="underline underline-offset-4">Support availability</Link>
              <Link to="/about" onClick={onClose} className="underline underline-offset-4">About LAVYN and getting started</Link>
              <Link to="/terms" onClick={onClose} className="underline underline-offset-4">Terms of Service</Link>
              <Link to="/privacy" onClick={onClose} className="underline underline-offset-4">Privacy Policy</Link>
            </div>
          </div>
        </motion.div>
      </div>}
    </AnimatePresence>
  );
}
